import type { RecipeEnemy } from '../api/missions';

/**
 * The enemy's skill as the wizard offers it: five named stops over the one
 * number Eden's slider sets, which the generator writes onto all nine
 * sub-skills (EnemySkill on the platform). 0,20 · 0,30 · 0,40 are the
 * generator's own light, medium and heavy; 0,55 is what the group's hand-made
 * missions give a few specialists, and 0,75 is above anything they stand.
 * The names are the web app's; the range and the default are the platform's.
 */
export const SKILL_STOPS = [
  { id: 'recruits', value: 0.2 },
  { id: 'regulars', value: 0.3 },
  { id: 'veterans', value: 0.4 },
  { id: 'elite', value: 0.55 },
  { id: 'relentless', value: 0.75 },
] as const;

export type SkillStop = (typeof SKILL_STOPS)[number]['id'];

/** What the range is read as until the platform says it (`RecipeView.enemy`), and from an older one. */
const RANGE_UNTIL_TOLD = { min: 0.1, max: 1, default: 0.3 };

export interface SkillRange {
  min: number;
  max: number;
  default: number;
}

export function skillRange(enemy: RecipeEnemy | null | undefined): SkillRange {
  return enemy
    ? { min: enemy.skillMin, max: enemy.skillMax, default: enemy.skillDefault }
    : RANGE_UNTIL_TOLD;
}

/** A skill as a person reads it here: two decimals and a comma. */
export const formatSkill = (n: number) => n.toFixed(2).replace('.', ',');

/** A typed value as a number: a comma or a point, nothing else; null when it is not one. */
export function parseSkill(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
}

export const clampSkill = (n: number, range: SkillRange) =>
  Math.round(Math.max(range.min, Math.min(range.max, n)) * 100) / 100;

/** The stop a value sits exactly on, when it does. */
export function stopOf(value: number): SkillStop | null {
  return SKILL_STOPS.find((s) => Math.abs(s.value - value) < 0.005)?.id ?? null;
}

/** The stop a value is nearest to: what an in-between value is described as. */
export function nearestStop(value: number): SkillStop {
  let best: (typeof SKILL_STOPS)[number] = SKILL_STOPS[0];
  for (const s of SKILL_STOPS) {
    if (Math.abs(s.value - value) < Math.abs(best.value - value)) best = s;
  }
  return best.id;
}
